import { logBreadcrumb } from 'shared/utils/sentry/sentry';

// Valid domains that our app should handle internally
const APP_DOMAINS = [
  'uat-bank.varomoney.com',
  'bank.varomoney.com',
  'ablink.bank.varomoney.com',
  'prod-cruise-web.varomoney.com',
  'acme',
  'localhost',
];

/**
 * Check if a URL should be handled by our app
 *
 * @param url - URL to check
 * @returns True if URL should be handled internally
 */
export const isAppDeepLink = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    // First, check the scheme
    if (urlObj.protocol === 'acme:') {
      return true;
    }
    // For https:// URLs, check the hostname
    return APP_DOMAINS.some((domain) => urlObj.hostname === domain);
  } catch {
    return false;
  }
};

/**
 * Resolves ABLinks and other manually fetched links to their final destination URLs.
 *
 * @param url The URL to resolve
 * @returns The resolved destination URL
 */

export const resolveManuallyFetchedLink = async (
  url: string,
): Promise<string> => {
  logBreadcrumb({
    level: 'info',
    message: 'Resolving link',
    data: { url, isABLink: isABLink(url), isOnelink: isOnelink(url) },
  });

  try {
    let resolvedUrl = url;

    // Resolve ABLinks and OneLinks to their final destination
    if (isABLink(url) || isOnelink(url)) {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `Failed to resolve link: ${response.status} ${response.statusText}`,
        );
      }

      logBreadcrumb({
        level: 'info',
        message: 'Fetch completed',
        data: { responseUrl: response.url, status: response.status },
      });

      if (isOnelink(url)) {
        // OneLinks use JavaScript redirects - parse HTML for store_link
        const html = await response.text();
        const regex = /var\s+store_link\s*=\s*['"]([^'"]+)['"]/;
        const match = html.match(regex);
        if (match && match[1]) {
          resolvedUrl = match[1];
          logBreadcrumb({
            level: 'info',
            message: 'OneLink resolved via HTML parsing',
            data: { resolvedUrl },
          });
        }
      } else {
        // ABLinks use HTTP redirects - response.url has the final destination
        resolvedUrl = response.url;
        logBreadcrumb({
          level: 'info',
          message: 'ABLink resolved via HTTP redirect',
          data: { resolvedUrl },
        });
      }
    }

    // Check if the resolved URL should be handled by our app
    const shouldHandleInternally = isAppDeepLink(resolvedUrl);

    logBreadcrumb({
      level: 'info',
      message: 'Link resolution complete',
      data: { originalUrl: url, resolvedUrl, shouldHandleInternally },
    });

    return resolvedUrl;
  } catch (error) {
    logBreadcrumb({
      level: 'error',
      message: 'Error resolving link',
      data: {
        url,
        error: error instanceof Error ? error.message : String(error),
      },
    });
    throw new Error(`Error resolving ABLink.`);
  }
};

/**
 * Check if URL is a Braze ABLink
 *
 * @param url - URL to check
 * @returns True if URL is an ABLink
 */
export const isABLink = (url: string): boolean => {
  return url.startsWith('https://ablink.bank.varomoney.com') ? true : false;
};

/**
 * Check if URL is an AppsFlyer OneLink
 *
 * @param url - URL to check
 * @returns True if URL is a OneLink
 */
export const isOnelink = (url: string): boolean => {
  return url.startsWith('https://varomoney.onelink.me') ? true : false;
};
